/**
 * useToast Hook - Toast Notifications
 *
 * Provides toast notification functionality for the application.
 * Integrates with @yak/ui Toast component when available.
 */

'use client';

import { useState, useCallback, createContext, useContext, useRef } from 'react';

import { generateId } from '~/lib/utils';

/**
 * Toast variant types
 */
export type ToastVariant = 'default' | 'destructive' | 'success' | 'warning';

/**
 * Toast action
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Toast data structure
 */
export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

/**
 * Toast creation input
 */
export interface ToastInput {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

/**
 * Toast context value
 */
export interface ToastContextValue {
  toasts: Toast[];
  toast: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

/**
 * Default toast duration in milliseconds
 */
const DEFAULT_DURATION = 5000;

/**
 * Maximum number of toasts to show at once
 */
const MAX_TOASTS = 5;

/**
 * Toast context
 */
const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook to access toast functionality
 *
 * @returns Toast context value
 * @throws Error if used outside ToastProvider
 *
 * @example
 * function SaveButton({ onSave }: { onSave: () => Promise<void> }) {
 *   const { toast } = useToast();
 *
 *   const handleSave = async () => {
 *     try {
 *       await onSave();
 *       toast({
 *         title: 'Saved',
 *         description: 'Your changes have been saved.',
 *         variant: 'success',
 *       });
 *     } catch (error) {
 *       toast({
 *         title: 'Error',
 *         description: 'Failed to save changes.',
 *         variant: 'destructive',
 *       });
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * }
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    // Return a fallback implementation if not in provider
    // This allows the hook to work even without the provider
    return createFallbackToast();
  }

  return context;
}

/**
 * Create fallback toast implementation
 * Uses state-based toasts that work without provider
 */
function createFallbackToast(): ToastContextValue {
  // Use a module-level state for toasts
  const toasts: Toast[] = [];

  return {
    toasts,
    toast: (input: ToastInput): string => {
      const id = generateId();
      const toast: Toast = {
        id,
        title: input.title,
        description: input.description,
        variant: input.variant || 'default',
        duration: input.duration || DEFAULT_DURATION,
        action: input.action,
      };

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        const icon = {
          default: 'ℹ️',
          success: '✅',
          warning: '⚠️',
          destructive: '❌',
        }[toast.variant];
        console.log(`${icon} Toast: ${toast.title}`, toast.description || '');
      }

      return id;
    },
    dismiss: () => {},
    dismissAll: () => {},
  };
}

/**
 * Hook to manage toast state (for ToastProvider)
 *
 * @returns Toast state and actions
 */
export function useToastState(): ToastContextValue {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /**
   * Add a new toast
   */
  const toast = useCallback((input: ToastInput): string => {
    const id = generateId();
    const newToast: Toast = {
      id,
      title: input.title,
      description: input.description,
      variant: input.variant || 'default',
      duration: input.duration || DEFAULT_DURATION,
      action: input.action,
    };

    setToasts((prev) => {
      // Remove oldest toasts if we're at the limit
      const updated = prev.length >= MAX_TOASTS ? prev.slice(1) : prev;
      return [...updated, newToast];
    });

    // Set timeout for auto-dismiss
    if (newToast.duration && newToast.duration > 0) {
      const timeout = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timeoutsRef.current.delete(id);
      }, newToast.duration);

      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, []);

  /**
   * Dismiss a specific toast
   */
  const dismiss = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));

    // Clear timeout if exists
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback((): void => {
    setToasts([]);

    // Clear all timeouts
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();
  }, []);

  return {
    toasts,
    toast,
    dismiss,
    dismissAll,
  };
}

/**
 * Toast provider component
 */
export { ToastContext };

/**
 * Shorthand methods for common toast types
 */
export function useToastHelpers() {
  const { toast } = useToast();

  return {
    /**
     * Show a success toast
     */
    success: useCallback(
      (title: string, description?: string) =>
        toast({ title, description, variant: 'success' }),
      [toast]
    ),

    /**
     * Show an error toast
     */
    error: useCallback(
      (title: string, description?: string) =>
        toast({ title, description, variant: 'destructive' }),
      [toast]
    ),

    /**
     * Show a warning toast
     */
    warning: useCallback(
      (title: string, description?: string) =>
        toast({ title, description, variant: 'warning' }),
      [toast]
    ),

    /**
     * Show an info toast
     */
    info: useCallback(
      (title: string, description?: string) =>
        toast({ title, description, variant: 'default' }),
      [toast]
    ),

    /**
     * Show a promise-based toast (loading -> success/error)
     */
    promise: useCallback(
      async <T,>(
        promise: Promise<T>,
        messages: {
          loading: string;
          success: string;
          error: string;
        }
      ): Promise<T> => {
        const loadingId = toast({
          title: messages.loading,
          variant: 'default',
          duration: 0, // Don't auto-dismiss
        });

        try {
          const result = await promise;
          toast({
            title: messages.success,
            variant: 'success',
          });
          return result;
        } catch (error) {
          toast({
            title: messages.error,
            variant: 'destructive',
          });
          throw error;
        }
      },
      [toast]
    ),
  };
}
